// /src/data/books/DarkForest/data/cap3.js
const CAP3 = {
  id: 3,
  slug: "cap3-crear-billetera",
  title: "Capítulo 3 — Crear una billetera",
  sections: [
    {
      heading: "Núcleo: la clave privada / frase semilla",
      text: [
        "El corazón de cualquier billetera es la **clave privada** (o la **frase mnemotécnica/seed** de la que se derivan las claves). Si la clave privada se pierde o es robada, **pierdes tu identidad y el control sobre los fondos**.",
        "La interfaz de la billetera (app, extensión, hardware) puede cambiar, las redes pueden actualizarse, las DApps pueden desaparecer, pero mientras conserves **tu seed bien protegida**, puedes reconstruir tu billetera en otro dispositivo o incluso en otra aplicación.",
        "Por eso, en este libro siempre que hablemos de ‘crear una billetera’ queremos que pienses en realidad: **estoy generando una clave maestra que me va a acompañar años y que debo proteger mejor que cualquier contraseña.**"
      ]
    },
    {
      heading: "¿Qué son exactamente?",
      list: [
        "**Clave privada**: número aleatorio grande que te identifica y autoriza transacciones. Es la pieza que nunca debe salir de tu control. Quien la tenga, puede firmar en tu nombre.",
        "**Frase semilla (mnemonic)**: lista de palabras legibles por humanos que permite regenerar tus claves (estándar **BIP-39**). Es una representación amigable de la clave maestra interna.",
        "**Dirección**: representación pública para recibir fondos, derivada de la clave pública. Puedes compartirla; es como tu ‘número de cuenta’ público.",
        "**Regla de oro**: **quien controla la clave privada controla los fondos**. Da igual la excusa, la oferta o la urgencia: si alguien te pide la seed o la clave privada, está pidiendo **control total sobre tu dinero**."
      ]
    },
    {
      heading: "Ejemplos demostrativos",
      // Usa 'pre' como string para evitar objetos como children
      pre:
`Private Key (demo):
0xa164d4767469de4faf09793ceea07d5a2f5d3cef7f6a9658916c581829ff5584

Seed Phrase (demo):
cruel weekend spike point innocent dizzy alien use evoke shed adjust wrong`,
      note:
        "Ejemplos con Ethereum solo para ilustrar. **¡Nunca uses claves/semillas de ejemplo!** Genera las tuyas de forma segura y fuera de línea cuando sea posible."
    },
    {
      heading: "Tipos de billeteras",
      text: [
        "No existe una sola billetera ‘perfecta’. Cada tipo tiene **ventajas, riesgos y casos de uso**. Lo importante es que elijas con criterio en función de tu realidad y del propósito que definiste."
      ],
      list: [
        "**PC / Escritorio**: control total; requiere **buena higiene del sistema** (actualizaciones, antivirus, no instalar cualquier cosa, separar usuarios). Suele usarse para operaciones más avanzadas o gestión de varios perfiles.",
        "**Extensión de navegador**: cómoda para DApps; **eleva el riesgo del navegador**. Ideal para DeFi, NFT, uso intensivo de Web3, pero solo si el navegador está endurecido (pocas extensiones, actualizaciones al día, cuidado con phishing).",
        "**Móvil**: conveniente; ideal para **montos pequeños y uso diario**. El riesgo se concentra en tu dispositivo: malware, pérdida o robo físico del teléfono.",
        "**Hardware wallet**: clave privada aislada en dispositivo dedicado; **recomendada para patrimonio** y ahorros de mediano-largo plazo. Minimiza la exposición al malware del sistema operativo.",
        "**Web wallet**: custodial o no-custodial vía web; debes **evaluar los riesgos del proveedor** (reputación, historial de incidentes, políticas de retiro, niveles de KYC, etc.). Útil para principiantes, pero exige más confianza en terceros."
      ]
    },
    {
      heading: "Conexión a Internet",
      text: [
        "Además del formato (móvil, extensión, hardware, etc.) es clave entender **cómo se conecta la billetera a la red**. Esto define su exposición diaria al riesgo."
      ],
      list: [
        "**Billetera fría (Cold)**: sin conexión directa; firma **offline** (más segura para ahorro). Ideal para montos altos y para fondos que no necesitas mover cada día.",
        "**Billetera caliente (Hot)**: conectada para operar con DApps; **mayor superficie de ataque** (malware, phishing, páginas clonadas). Adecuada para uso frecuente y montos acotados."
      ]
    },
    {
      heading: "Empieza por el propósito",
      text: [
        "Antes de instalar nada, pregúntate: **¿para qué quiero esta billetera?**",
        "No es lo mismo una billetera para:",
        "- **Ahorro a largo plazo** (hold de BTC/ETH u otros activos principales).",
        "- **Trading activo** (entrar y salir de posiciones con frecuencia).",
        "- **Pruebas y experimentación** (DApps nuevas, redes de testnet, contratos poco conocidos).",
        "- **NFTs** (colección, minteo, mercados secundarios).",
        "- **DeFi** (liquidez, préstamos, yield farming, etc.).",
        "El propósito determina **qué tipo de wallet elegir**, **cómo configurarla** y **qué controles aplicar**. Por ejemplo, una billetera para pruebas puede ser móvil y con pocos controles, pero una para patrimonio debería ser hardware/cold con un ritual de respaldo muy cuidado."
      ]
    },
    {
      heading: "Principio de aislamiento",
      text: [
        "Un error frecuente es usar **la misma billetera para todo**: pruebas, juegos, DeFi arriesgoso, NFT experimentales y ahorros serios. Esto concentra el riesgo y hace que cualquier fallo te afecte al 100%.",
        "La idea del **aislamiento** es sencilla: separar funciones para que un problema en un área **no arrastre todo tu patrimonio**."
      ],
      list: [
        "No pongas **todos los huevos en la misma canasta**.",
        "Cuanto **más uses** una billetera, **más riesgo** asume (más firmas, más conexiones, más DApps).",
        "Crea billeteras separadas por función: **pruebas**, **gastos diarios**, **patrimonio (fría)**.",
        "Al probar algo nuevo, usa **una billetera aparte y con fondos mínimos**.",
        "Para NFTs y DeFi de riesgo alto, considera una billetera dedicada que **no tenga acceso al grueso de tus fondos**."
      ]
    },
    {
      heading: "Procedimiento recomendado (paso a paso)",
      text: [
        "Este procedimiento resume una forma **prudente** de crear tu primera billetera (o migrar a una más segura). Adáptalo según tu nivel, pero intenta respetar al menos la **estructura general**."
      ],
      list: [
        "1. **Prepara el entorno:** sistema actualizado, navegador limpio, sin extensiones innecesarias. Evita redes WiFi públicas para este proceso.",
        "2. **Descarga desde fuentes oficiales y verifica checksum/firmas:** ve siempre al sitio oficial del proyecto, revisa que el dominio sea correcto y, si el proyecto lo ofrece, verifica las firmas o hashes de la descarga.",
        "3. **Crea la billetera en modo seguro:** si usas hardware/PC para generar la seed, considera **desactivar Internet** durante la generación de la frase semilla, cerrar aplicaciones innecesarias y hacerlo en un entorno tranquilo.",
        "4. **Anota la seed en papel/metal (sin capturas de pantalla):** nunca guardes la seed en fotos, notas sin cifrar o chats. Usa papel o placas metálicas; si puedes, considera agregar una **passphrase** adicional.",
        "5. **Prueba una restauración con fondos mínimos:** envía una pequeña cantidad, restaura la billetera en otro dispositivo o app compatible y verifica que puedes recuperar el acceso. Es mejor descubrir errores de copia cuando aún no hay patrimonio serio involucrado.",
        "6. **Configura contraseñas fuertes, bloqueo automático y 2FA donde aplique:** si la app lo permite, activa mecanismos de bloqueo, PIN, biometría y, en servicios custodiales, **2FA robusto (no SMS).**",
        "7. **Separa billeteras por propósito y límites de saldo:** establece desde el inicio qué billetera es para qué (pruebas, gastos, ahorro) y qué montos máximos guardas en cada una.",
        "8. **Documenta tu propio ‘ritual’:** escribe para ti misma/o los pasos clave que seguirás siempre que crees una nueva billetera o hagas un respaldo. Esto reduce improvisaciones cuando estés bajo presión."
      ]
    },
    {
      heading: "Cierre",
      text: [
        "La **elección de la billetera y su configuración** definen gran parte de tu **superficie de ataque**. No todo se resuelve con ‘una buena hardware wallet’; lo que realmente marca la diferencia es **cómo piensas la estructura de tus billeteras**, cómo respaldas tus semillas y cómo separas usos por nivel de riesgo.",
        "A medida que ganes experiencia, descubrirás que **una sola billetera nunca es suficiente**: vas a necesitar un pequeño ‘set’ diseñado con **aislamiento, respaldos y controles claros**.",
        "En los siguientes capítulos iremos conectando esta base con escenarios cada vez más específicos (DeFi, NFT, permisos avanzados, phishing sofisticado), pero todo se sostiene sobre lo que has visto aquí: **una billetera bien creada, bien respaldada y bien ubicada en tu estrategia personal.**"
      ]
    }
  ]
};

export default CAP3;
